import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Top-level Error Boundary that prevents the entire app from showing a blank white page
 * when an unhandled runtime error occurs during rendering.
 *
 * Uses ONLY inline styles (zero external dependencies) so it works even when
 * CSS-in-JS libraries or stylesheets fail to load.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/home';
  };

  private handleDismiss = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            padding: 24,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              padding: '48px 40px',
              maxWidth: 480,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#FFF0F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            {/* Title */}
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px' }}>
              Something went wrong
            </h1>

            {/* Subtitle */}
            <p style={{ fontSize: 16, color: '#6c757d', margin: '0 0 32px', lineHeight: 1.5 }}>
              An unexpected error occurred. Don&apos;t worry &mdash; your data is safe.
            </p>

            {/* Dev error details */}
            {isDev && this.state.error && (
              <div
                style={{
                  background: '#FFF8F0',
                  border: '1px solid #FFD6B0',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 32,
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontFamily: "'SF Mono', 'Fira Code', monospace",
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#D63031',
                    marginBottom: 8,
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.name}: {this.state.error.message}
                </div>
                {this.state.errorInfo?.componentStack && (
                  <pre
                    style={{
                      fontFamily: "'SF Mono', 'Fira Code', monospace",
                      fontSize: 11,
                      color: '#856404',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {this.state.errorInfo.componentStack
                      .split('\n')
                      .filter((line) => line.trim())
                      .slice(0, 8)
                      .join('\n')}
                  </pre>
                )}
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={this.handleReload}
                style={{
                  background: '#00dda2',
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  padding: '14px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                style={{
                  background: 'transparent',
                  color: '#00dda2',
                  border: '2px solid #00dda2',
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Go to Home
              </button>

              <button
                onClick={this.handleDismiss}
                style={{
                  background: 'transparent',
                  color: '#6c757d',
                  border: 'none',
                  borderRadius: 12,
                  padding: '10px 24px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Try to Continue
              </button>
            </div>

            {/* Branding */}
            <div
              style={{
                marginTop: 32,
                fontSize: 13,
                fontWeight: 700,
                color: '#dee2e6',
                letterSpacing: 2,
              }}
            >
              KINASTIC
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
